import {
  Instagram,
  Twitter,
  Facebook,
  Youtube,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";

export interface SignatureProfileFooterData {
  logoSrc: string;
  brandName: string;
  social: {
    icon: "instagram" | "twitter" | "facebook" | "youtube";
    href: string;
  }[];
  columns: { title: string; links: { label: string; href: string }[] }[];
  contact: { address: string[]; phone: string; email: string };
  bottomLinks: { label: string; href: string }[];
  copyright: string;
}

export const SignatureProfileFooter: React.FC<{
  data: SignatureProfileFooterData;
}> = ({ data }) => (
  <footer className="bg-artist-charcoal text-white pt-16 pb-8">
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
        <div>
          <a className="flex items-center gap-2" href="/">
            <img alt="Artify" className="h-8 w-auto" src={data.logoSrc} />
            <span className="font-playfair text-xl font-light">
              {data.brandName}
            </span>
          </a>
          <p className="mt-4 text-gray-400">
            Artstorefronts connects exceptional artists with collectors and
            enthusiasts worldwide. Discover contemporary art that moves and
            inspires.
          </p>
          <div className="mt-6 flex gap-4">
            {data.social.map((s) => (
              <a
                key={s.icon}
                href={s.href}
                className="text-gray-400 hover:text-white"
              >
                {s.icon === "instagram" && <Instagram size={20} />}
                {s.icon === "twitter" && <Twitter size={20} />}
                {s.icon === "facebook" && <Facebook size={20} />}
                {s.icon === "youtube" && <Youtube size={20} />}
              </a>
            ))}
          </div>
        </div>

        {data.columns.map((col) => (
          <div key={col.title}>
            <h3 className="font-playfair text-lg font-medium mb-4">
              {col.title}
            </h3>
            <ul className="space-y-2">
              {col.links.map((l) => (
                <li key={l.label}>
                  <a className="text-gray-400 hover:text-white" href={l.href}>
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h3 className="font-playfair text-lg font-medium mb-4">Contact</h3>
          <ul className="space-y-4">
            <li className="flex items-start">
              <MapPin className="mr-2 mt-1 text-artist-accent" size={18} />
              <span>
                {data.contact.address.map((line, idx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={idx}>
                    {line}
                    <br />
                  </span>
                ))}
              </span>
            </li>
            <li className="flex items-center">
              <Phone className="mr-2 text-artist-accent" size={18} />
              <span>{data.contact.phone}</span>
            </li>
            <li className="flex items-center">
              <Mail className="mr-2 text-artist-accent" size={18} />
              <span>{data.contact.email}</span>
            </li>
          </ul>
        </div>
      </div>
      <hr className="border-gray-800 mb-8" />
      <div className="flex flex-col md:flex-row justify-between items-center text-gray-500 text-sm">
        <p>{data.copyright}</p>
        <div className="flex gap-6 mt-4 md:mt-0">
          {data.bottomLinks.map((l) => (
            <a key={l.label} className="hover:text-white" href={l.href}>
              {l.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  </footer>
);
